import {
  useErrDocumentNotReady,
  useSetErrDocumentNotReadyFromRtk,
} from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import { Separator } from "@/shared/components/ui/separator";
import {
  DocumentNotReadyAlert,
  DocumentSheet,
  DocumentSheetActions,
  DocumentSheetActionsContent,
  DocumentSheetActionsDownload,
  DocumentSheetActionsHeader,
  DocumentSheetContent,
  DocumentSheetHeader,
  DocumentSheetImmutabilityWarning,
  DocumentSheetMain,
  DocumentSheetPreview,
  DocumentSheetTitle,
  DocumentSheetTrigger,
} from "@/shared/bsm/core/document/components/ui/document-sheet";
import { Download } from "lucide-react";
import { useDownloadPdf } from "@/shared/lib/utilities/download-pdf";
import { useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  useVbsWorkOrderDownloadPDFMutation,
  useVbsWorkOrderPreviewPDFQuery,
  WorkOrderDocument,
} from "@/services/backend/vbs/work-orders/api/work-order-api";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";

export function WorkOrderPdf({ workOrderId }: { workOrderId: string }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading, error } = useVbsWorkOrderPreviewPDFQuery(
    {
      id: workOrderId,
      document: WorkOrderDocument.All,
    },
    {
      skip: !open,
    },
  );

  const { notReadyError } = useErrDocumentNotReady();

  return (
    <DocumentSheet open={open} onOpenChange={setOpen}>
      <DocumentSheetTrigger asChild>
        <Button variant="outline">PDF</Button>
      </DocumentSheetTrigger>
      <DocumentSheetContent className="max-h-[90vh]">
        <DocumentSheetHeader>
          <DocumentSheetTitle>{t("Download PDF")}</DocumentSheetTitle>
        </DocumentSheetHeader>
        <DocumentSheetMain>
          <DocumentSheetPreview
            objectURL={data?.objectURL}
            isLoading={isLoading}
            error={error}
          />
          <DocumentSheetActions>
            <DocumentSheetActionsHeader
              title={t("Arbeitsschein")}
              documentNumber={<DocumentNumberById documentId={workOrderId} />}
            />
            <DocumentSheetActionsContent>
              <DocumentNotReadyAlert
                error={notReadyError}
                document={t("Arbeitsschein")}
              />
              <DocumentSheetImmutabilityWarning
                title={t("Hinweis")}
                description={t(
                  "Arbeitsscheine werden nicht festgeschrieben. Sie können sie jederzeit herunterladen.",
                )}
              />
              <Separator />
              <DocumentSheetActionsDownload>
                <DownloadDocumentButton
                  workOrderId={workOrderId}
                  document={WorkOrderDocument.All}
                  text={t("Alle Dokumente")}
                />
                <DownloadDocumentButton
                  workOrderId={workOrderId}
                  document={WorkOrderDocument.WorkOrder}
                  text={t("Arbeitsschein")}
                />
                <DownloadDocumentButton
                  workOrderId={workOrderId}
                  document={WorkOrderDocument.Documentation}
                  text={t("Dokumentation")}
                />
                <DownloadDocumentButton
                  workOrderId={workOrderId}
                  document={WorkOrderDocument.LastMinuteRiskAnalysis}
                  text={t("LMRA")}
                />
              </DocumentSheetActionsDownload>
            </DocumentSheetActionsContent>
          </DocumentSheetActions>
        </DocumentSheetMain>
      </DocumentSheetContent>
    </DocumentSheet>
  );
}

function DownloadDocumentButton({
  workOrderId,
  document: documentType,
  text,
}: {
  workOrderId: string;
  document: WorkOrderDocument;
  text: string;
}) {
  const [open, setOpen] = useState(false);
  const [download, { isLoading, error, data, reset }] =
    useVbsWorkOrderDownloadPDFMutation();

  useDownloadPdf(data, () => {
    setOpen(false);
    reset();
  });

  useSetErrDocumentNotReadyFromRtk(error, () => {
    setOpen(false);
    reset();
  });

  const doDownload = () => {
    if (isLoading) {
      return;
    }

    download({
      id: workOrderId,
      document: documentType,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="justify-start">
          <Download className="mr-4 h-5 w-5" /> {text}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Arbeitsschein herunterladen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Wenn ein Arbeitsschein heruntergeladen wird, wird er festgeschrieben. Er kann dann nicht mehr geändert werden.",
            )}
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doDownload} disabled={isLoading}>
            {isLoading ? (
              <Download className="mr-4 h-5 w-5 animate-bounce" />
            ) : (
              <Download className="mr-4 h-5 w-5" />
            )}
            <span>{t("Herunterladen")}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
